import React, {useEffect} from 'react'
import {RemoveScroll} from 'react-remove-scroll'
import {Box, BoxProps, useMultiStyleConfig} from '@chakra-ui/react'
import {HeaderProvider, useHeaderState} from '_context'
import {useViewport, useCustomApi, useAnalytics, useOpenModal} from '_customHooks'
import Topbar from './partials/topbar'
import SearchSection from './partials/search-section'
import Sidebar from './partials/sidebar'
import {Banner} from './partials/banner'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCookies} from 'react-cookie'
import {useUsid, useShopperContext} from '@salesforce/commerce-sdk-react'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import AccessibilityBanner from './partials/accessibilityBanner'
import {SkipNavLink} from '@chakra-ui/skip-nav'

/**
 * @name Header
 *
 * @component
 *
 * @description
 * Manages the render of the topbar, menu, banner, sidebar and search section.
 *
 * @remarks
 * - The locale modal is handled here because it's related to a global context.
 * - Usage of `useHeaderState` here could affect performance; if you add some new states to the context remember
 * to memoize the components that will be using them to avoid re-renders related issues.
 * - SkinNavLink should be after the usableNet Banner
 *
 * @returns {React.FC} - Rendered `Header` component.
 */
const Header: React.FC<{shopperContext?: {clientIp?: string}}> = ({shopperContext}) => {
    const {headerRef, isSearchSectionEnabled, isSidebarOpen, isTransparentMode, isAtTop, showBanner, isHomepage} = useHeaderState() // TODO check here isTransparentMode and isHomepage
    const {openSwitchLocaleModal} = useOpenModal()
    const {isDesktop} = useViewport()
    const {site, locale} = useMultiSite()
    const [cookies, setCookie] = useCookies()
    const {usid} = useUsid()
    const {analyticsEventCheckLocale} = useAnalytics()
    const {useVerifyGeolocation} = useCustomApi()

    const {data: fetchedShopperContext} = useShopperContext({parameters: {usid, siteId: site.id}}, {enabled: !isServer && Boolean(usid) && !shopperContext})
    const finalShopperContext = shopperContext || fetchedShopperContext

    const {data: geolocation} = useVerifyGeolocation(locale.id, usid, finalShopperContext?.clientIp)

    const styles = useMultiStyleConfig('Header', {isTransparentMode}) as Record<string, BoxProps>
    const hasCountrySelection = 'bc_fcountry' in cookies
    const hasSelectedCountry = 'bc_countryselection' in cookies

    const triggerSwitchLocaleModal = () => {
        openSwitchLocaleModal({
            labels: geolocation?.assets || [],
            locale: geolocation?.locale || locale,
            onClose: () => {
                const currentLocale = geolocation?.locale?.current.value

                setCookie('bc_countryselection', currentLocale.split('_')[1], {path: '/'})
                analyticsEventCheckLocale({
                    action: 'close',
                    label: currentLocale.split('_')[1],
                })
            },
        })
    }

    useEffect(() => {
        if (geolocation?.suggestCountryChange) {
            if (hasCountrySelection || hasSelectedCountry) {
                const cookieCountryCode = cookies['bc_fcountry']
                const cookieSelectedCountry = cookies['bc_countryselection']
                const activeCountryCode = locale.id.split('-')[1]

                if (cookieSelectedCountry === activeCountryCode || cookieCountryCode === activeCountryCode) return

                triggerSwitchLocaleModal()
            } else {
                triggerSwitchLocaleModal()
            }
        }
    }, [geolocation])

    return (
        <RemoveScroll enabled={isSidebarOpen && !isDesktop} noIsolation={true}>
            <Box ref={headerRef} data-testid="header" {...styles.header} as="header">
                <AccessibilityBanner isAtTop={isAtTop} />

                <SkipNavLink zIndex="skipLink">Skip to Content</SkipNavLink>

                <Topbar isAtTop={isAtTop} isTransparentMode={isTransparentMode} isSearchSectionEnabled={isSearchSectionEnabled} isHomepage={isHomepage} />

                {showBanner && <Banner />}

                {isSidebarOpen && !isDesktop && <Sidebar />}

                {isSearchSectionEnabled && <SearchSection isAtTop={isAtTop} />}
            </Box>
        </RemoveScroll>
    )
}

/**
 * @name HeaderWithProvider
 *
 * @component
 *
 * @description
 * Wraps the Header component with HeaderProvider.
 * This ensures that the Header component has access to the Header context.
 *
 * @returns {React.FC} The Header component wrapped with HeaderProvider.
 */
const HeaderWithProvider: React.FC<{shopperContext?: {clientIp?: string}}> = ({shopperContext}) => {
    return (
        <HeaderProvider>
            <Header shopperContext={shopperContext} />
        </HeaderProvider>
    )
}

export {HeaderWithProvider as Header}
